import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  Stack,
  IconButton,
} from '@mui/material';
import {
  Event as EventIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Link as LinkIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import { apiService } from '../../../services/api';
import CustomDateTimePicker from '../../../components/common/CustomDateTimePicker';

interface CreateSessionsModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  onSessionsCreated: () => void;
}

interface SessionFormData {
  topic: string;
  description: string;
  startDateTime: Dayjs | null;
  endDateTime: Dayjs | null;
  presenter: string;
  presenterEmail: string;
  otherAttendeeEmails: string[];
  resourceLinks: string[];
}

const CreateSessionsModal: React.FC<CreateSessionsModalProps> = ({
  open,
  onClose,
  userId,
  userName,
  onSessionsCreated,
}) => {
  const [formData, setFormData] = useState<SessionFormData>({
    topic: '',
    description: '',
    startDateTime: null,
    endDateTime: null,
    presenter: '',
    presenterEmail: '',
    otherAttendeeEmails: [''],
    resourceLinks: [''],
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [duration, setDuration] = useState<number>(0);

  useEffect(() => {
    if (open) {
      // Reset form when modal opens
      const tomorrow = dayjs().add(1, 'day').hour(9).minute(0).second(0);
      const endTime = tomorrow.hour(10);

      setFormData({
        topic: '',
        description: '',
        startDateTime: tomorrow,
        endDateTime: endTime,
        presenter: '',
        presenterEmail: '',
        otherAttendeeEmails: [''],
        resourceLinks: [''],
      });
      setError(null);
      setEmailError(null);
      setDuration(1);
    }
  }, [open]);

  useEffect(() => {
    // Calculate duration when start or end time changes
    if (formData.startDateTime && formData.endDateTime) {
      const diffHours = formData.endDateTime.diff(formData.startDateTime, 'hour', true);
      setDuration(Math.max(0, diffHours));
    } else {
      setDuration(0);
    }
  }, [formData.startDateTime, formData.endDateTime]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (field: keyof SessionFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Validate presenter email
    if (field === 'presenterEmail') {
      if (value && !validateEmail(value)) {
        setEmailError('Please enter a valid email address');
      } else {
        setEmailError(null);
      }
    }
  };

  const handleArrayFieldChange = (field: 'otherAttendeeEmails' | 'resourceLinks', index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item),
    }));
  };

  const addArrayField = (field: 'otherAttendeeEmails' | 'resourceLinks') => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], ''],
    }));
  };

  const removeArrayField = (field: 'otherAttendeeEmails' | 'resourceLinks', index: number) => {
    if (formData[field].length > 1) {
      setFormData(prev => ({
        ...prev,
        [field]: prev[field].filter((_, i) => i !== index),
      }));
    }
  };

  const validateForm = (): boolean => {
    if (!formData.topic.trim()) {
      setError('Topic is required');
      return false;
    }
    if (!formData.startDateTime) {
      setError('Start date and time is required');
      return false;
    }
    if (!formData.endDateTime) {
      setError('End date and time is required');
      return false;
    }
    
    if (formData.endDateTime.isBefore(formData.startDateTime) || formData.endDateTime.isSame(formData.startDateTime)) {
      setError('End time must be after start time');
      return false;
    }
    if (!formData.presenter.trim()) {
      setError('Presenter is required');
      return false;
    }
    if (!formData.presenterEmail.trim()) {
      setError('Presenter email is required');
      return false;
    }
    if (!validateEmail(formData.presenterEmail)) {
      setError('Please enter a valid presenter email address');
      return false;
    }

    // Validate other attendee emails (only if they're not empty)
    for (const email of formData.otherAttendeeEmails) {
      if (email.trim() && !validateEmail(email)) {
        setError('Please enter valid email addresses for other attendees');
        return false;
      }
    }

    setError(null);
    return true;
  };

  const handleCreateSession = async () => {
    if (!validateForm()) return;

    try {
      setCreating(true);
      setError(null);
      
      const sessionData = {
        onboardUserId: userId,
        topic: formData.topic.trim(),
        description: formData.description.trim(),
        startDateTime: formData.startDateTime!.toISOString(),
        endDateTime: formData.endDateTime!.toISOString(),
        duration: duration,
        presenter: formData.presenter.trim(),
        presenterEmail: formData.presenterEmail.trim(),
        employeeEmail: '', // This will be set by the backend
        otherAttendeeEmails: formData.otherAttendeeEmails.filter(email => email.trim()).join(';'),
        resourceLinks: formData.resourceLinks.filter(link => link.trim()).join(';'),
        sessionCompleted: false,
        confidenceLevel: undefined,
        employeeFeedback: '',
        lineManagerFeedback: '',
      };

      await apiService.createOnboardingSession(sessionData);
      
      // Call the callback to refresh the parent components
      onSessionsCreated();
      
      // Close the modal
      onClose();
    } catch (err) {
      console.error('Error creating session:', err);
      setError('Failed to create session');
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    if (!creating) {
      onClose();
    }
  };

  const formatDuration = (hours: number): string => {
    if (hours === 0) return '0 hours';
    if (hours < 1) return `${Math.round(hours * 60)} minutes`;
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    if (minutes === 0) return `${wholeHours} hour${wholeHours !== 1 ? 's' : ''}`;
    return `${wholeHours}h ${minutes}m`;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EventIcon color="primary" />
          <Box>
            <Typography variant="h6">
              Create Onboarding Session
            </Typography>
            <Typography variant="body2" color="text.secondary">
              for {userName}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Topic and Description */}
          <TextField
            fullWidth
            label="Topic"
            value={formData.topic}
            onChange={(e) => handleInputChange('topic', e.target.value)}
            placeholder="e.g., Company Introduction, IT Setup, Role Training"
            required
          />

          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Detailed description of the session content and objectives"
          />

          {/* Date and Time */}
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <CustomDateTimePicker
              label="Start Date & Time"
              value={formData.startDateTime}
              onChange={(newValue) => handleInputChange('startDateTime', newValue)}
              required
              fullWidth
            />

            <CustomDateTimePicker
              label="End Date & Time"
              value={formData.endDateTime}
              onChange={(newValue) => handleInputChange('endDateTime', newValue)}
              required
              fullWidth
            />
          </Box>

          {/* Duration Display */}
          {duration > 0 && (
            <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon color="primary" />
              <Typography variant="body2">
                <strong>Duration:</strong> {formatDuration(duration)}
              </Typography>
            </Box>
          )}

          {/* Presenter Information */}
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              fullWidth
              label="Presenter"
              value={formData.presenter}
              onChange={(e) => handleInputChange('presenter', e.target.value)}
              placeholder="Name of the session presenter"
              required
              InputProps={{
                startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} />,
              }}
            />

            <TextField
              fullWidth
              label="Presenter Email"
              type="email"
              value={formData.presenterEmail}
              onChange={(e) => handleInputChange('presenterEmail', e.target.value)}
              placeholder="presenter@company.com"
              required
              error={!!emailError}
              helperText={emailError}
              InputProps={{
                startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />,
              }}
            />
          </Box>

          {/* Other Attendee Emails */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon color="action" />
              Other Attendee Emails (Optional)
            </Typography>
            <Stack spacing={2}>
              {formData.otherAttendeeEmails.map((email, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="email"
                    value={email}
                    onChange={(e) => handleArrayFieldChange('otherAttendeeEmails', index, e.target.value)}
                    placeholder="attendee@company.com"
                  />
                  <IconButton
                    onClick={() => removeArrayField('otherAttendeeEmails', index)}
                    disabled={formData.otherAttendeeEmails.length === 1}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={() => addArrayField('otherAttendeeEmails')}
                variant="outlined"
                size="small"
                sx={{ alignSelf: 'flex-start' }}
              >
                Add Email
              </Button>
            </Stack>
          </Box>

          {/* Resource Links */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinkIcon color="action" />
              Resource Links (Optional)
            </Typography>
            <Stack spacing={2}>
              {formData.resourceLinks.map((link, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={link}
                    onChange={(e) => handleArrayFieldChange('resourceLinks', index, e.target.value)}
                    placeholder="https://company.com/resources"
                  />
                  <IconButton
                    onClick={() => removeArrayField('resourceLinks', index)}
                    disabled={formData.resourceLinks.length === 1}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={() => addArrayField('resourceLinks')}
                variant="outlined"
                size="small"
                sx={{ alignSelf: 'flex-start' }}
              >
                Add Link
              </Button>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose}
          disabled={creating}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleCreateSession}
          variant="contained"
          disabled={creating}
        >
          {creating ? 'Creating...' : 'Create Session'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateSessionsModal;
